import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { AbsenceStore } from '../../../store/Absence';
import { Modal, Button, Checkbox, Divider } from '@iba-team/portal-react-components/dist';
import { Accordion } from '../../../utils_components/Accordion/Accordion';
import NewListLink from '../../../utils_components/NewListLink';
import { PLANNER_ROUTE } from '../../../utils/consts';
import { Key } from 'antd/es/table/interface';
import { Tree } from 'antd';
import { DataNode } from 'antd/lib/tree';
import { TreeProps } from 'antd/es/tree';
import { DownOutlined } from '@ant-design/icons';
import { groupBy } from 'fp-ts/es6/NonEmptyArray';
import { NonEmptyArray } from 'fp-ts/NonEmptyArray';
import { AbsenceListItemI, AbsenceFilterDtoI, AbsenceFilterDtoExtI } from '../../../types';
import { ReactComponent as SmartPreferencesSvg } from '../../../assets/icons/smart-preferences.svg';
import { RestoreDefaultSettingsModal } from './RestoreDefaultSettingsModal';
import './AbsenceSettingsModal.scss';

export type AbsenceSettingsModalI = {
  onClose?: () => void,
  onSave: () => void,
}

const ALL = 'All';
const ROOT_DIVISIONS = 0;
const ROOT_CLASS = 'root-node';

type DataNodeExtended = DataNode & {
  type: string;
}

interface SettingsDataI {
  checkedKeys: Key[],
  tag: string,
  isAllSelected: boolean,
} 

const initialSettings: SettingsDataI = {
  checkedKeys: [],
  tag: '',
  isAllSelected: false,
};

const selectedTagText = (n: number) => `Selected: ${n}`;

function getFlattenTree(tree: AbsenceFilterDtoI[]) {
  let result: AbsenceFilterDtoExtI[] = [];
  // Iterate through each node in the tree
  tree.forEach(node => {
    result.push({
      listName: node.listName,
      listType: node.listType,
      plannerListId: node.plannerListId,
      selected: node.selected,
      childrenIds: node.subFilterDtos.map(i => i.plannerListId)
    });  // Add the node without its children
    if (node.subFilterDtos && node.subFilterDtos.length > 0) {
      // Recursively add children nodes if they exist (but they will not have children here)
      result = result.concat(getFlattenTree(node.subFilterDtos));
    }
  });

  return result;
}

export const AbsenceSettingsModal = observer(({onClose, onSave} : AbsenceSettingsModalI) => {
  const { t } = useTranslation();
  const { absenceSettings, getAbsenceSettings, updateAbsenceSettings, isAbsenceSettingsFetched } = AbsenceStore;

  const [allCirclesListsChosen,  setAllCirclesListsChosen] = useState<boolean>(false);
  const [allProjectListsChosen,  setAllProjectListsChosen] = useState<boolean>(false);
  const [allDivisionListsChosen, setAllDivisionListsChosen] = useState<boolean>(false);
  const [allPlannerListsChosen,  setAllPlannerListsChosen] = useState<boolean>(false);
  const [myLists, setMyLists] = useState<SettingsDataI>(initialSettings);
  const [myListsOpen, setMyListsOpen] = useState<boolean>(false);

  const [divisions, setDivisions] = useState<SettingsDataI>(initialSettings);
  const [divisionsOpen, setDivisionsOpen] = useState<boolean>(false);

  const [locations, setLocations] = useState<SettingsDataI>(initialSettings);
  const [locationsOpen, setLocationsOpen] = useState<boolean>(false);

  const [smartPreferencesChecked, setSmartPreferencesChecked] = useState<boolean>(false);
  const [displayNewListsChecked, setDisplayNewListsChecked] = useState<boolean>(false);

  const [defaultSettingsVisible, setDefaultSettingsVisible] = useState<boolean>(false);

  useEffect(() => {
    getAbsenceSettings();
  }, []);

  const absenceListWithOnlyLocations = useMemo(() => {
    return absenceSettings.filtersList.filter(i => i.listType === 'LOCATION');
  },[absenceSettings.filtersList]);

  const absenceListWithoutLocations = useMemo(() => {
    return absenceSettings.filtersList.filter(i => i.listType !== 'LOCATION' && i.listType !== 'ALL_LOCATIONS');
  },[absenceSettings.filtersList]);

  const divisionsFlattenTree = useMemo(() => {
    return getFlattenTree([absenceSettings.filtersDivisionsTree]);
  },[absenceSettings.filtersDivisionsTree]);

  const handleDivisionsChange = (keys: Key[]) => {
    let tag = '';

    const isAllSelected = keys.includes(0);
    if (isAllSelected) {
      tag = t('absenceSettingsModal.allDivisionsTag');
    } else if (keys.length === 1) {
      const foundTitle = divisionsFlattenTree.find(i => i.plannerListId === keys[0])?.listName;
      tag = foundTitle ? foundTitle : selectedTagText(keys.length);
    } else {
      const parentKeys = new Set(keys);
      for (const key of keys) {
        const childrenIds = divisionsFlattenTree.find(i => i.plannerListId === key)?.childrenIds;
        if (childrenIds !== undefined) {
          if (childrenIds.length) {
            for (const ch of childrenIds) {
              parentKeys.delete(ch);
            }
          }
        }
      }
      if (parentKeys.size === 1) {
        const [theOnlyParent] = parentKeys;
        const foundTitle =  divisionsFlattenTree.find(i => i.plannerListId === theOnlyParent)?.listName;
        tag = foundTitle ? foundTitle : selectedTagText(keys.length);
      } else {
        tag = selectedTagText(keys.length);
      }
    }

    setDivisions({
      checkedKeys: keys,
      tag: tag,
      isAllSelected: isAllSelected,
    });
  };

  const handleLocationsChange = (isAllSelected: boolean, checkedLocations: {key: Key, title: string}[]) => {
    let tag = '';

    if (isAllSelected) {
      tag = t('absenceSettingsModal.allLocationsTag');
    } else if (checkedLocations.length === 1) {
      tag = checkedLocations[0].title;
    } else {
      tag = selectedTagText(checkedLocations.length);
    }

    setLocations({
      checkedKeys: checkedLocations.map(i => i.key),
      tag: tag,
      isAllSelected: isAllSelected,
    });
  };

  const handleMyListsChange = (isAllSelected: boolean, checkedLists: {key: Key, title: string, type: string}[]) => {
    let tag = '';
    const checkedKeys = checkedLists.map(i => i.key);

    if (isAllSelected) {
      tag = t('absenceSettingsModal.allListsTag');
    } else if (!checkedLists.length) {
      tag = selectedTagText(0);
    } else if (!checkedLists.every(i => i.type === checkedLists[0].type)) {
      tag = selectedTagText(checkedLists.length);
    } else if (checkedLists.length === absenceListWithoutLocations.filter(i => i.listType === checkedLists[0].type).length) {
      tag = t(checkedLists[0].type);
    } else if (checkedLists.length === 1) {
      tag = checkedLists[0].title;
    } else {
      tag = selectedTagText(checkedLists.length);
    }

    const circlesLists = absenceListWithoutLocations.filter(i => i.listType === 'CIRCLE').map(p => p.plannerListId);
    const allCirclesSelected = circlesLists.every(i => checkedKeys.includes(i));
    setAllCirclesListsChosen(allCirclesSelected);

    const pmtLists = absenceListWithoutLocations.filter(i => i.listType === 'PROJECT').map(p => p.plannerListId);
    const allPmtSelected = pmtLists.every(i => checkedKeys.includes(i));
    setAllProjectListsChosen(allPmtSelected);

    const divisionLists = absenceListWithoutLocations.filter(i => i.listType === 'DIVISION').map(p => p.plannerListId);
    const allDivisionsSelected = divisionLists.every(i => checkedKeys.includes(i));
    setAllDivisionListsChosen(allDivisionsSelected);

    const plannerLists = absenceListWithoutLocations.filter(i => i.listType === 'PLANNER').map(p => p.plannerListId);
    const allPlannerSelected = plannerLists.every(i => checkedKeys.includes(i));
    setAllPlannerListsChosen(allPlannerSelected);
  
    setMyLists({
      checkedKeys: checkedKeys,
      tag: tag,
      isAllSelected: isAllSelected,
    });
  };

  useEffect(() => {
    const checkedLocations = absenceListWithOnlyLocations.filter(f => f.selected).map(i => ({key: i.plannerListId, title: i.listName}));
    const areAllLocationsSelected = absenceListWithOnlyLocations.every(i => i.selected);
    handleLocationsChange(areAllLocationsSelected, checkedLocations);

    const checkedLists = absenceListWithoutLocations.filter(f => f.selected).map(i => ({
      key: i.plannerListId,
      title: i.listName,
      type: i.listType
    }));
    const areAllListsSelected = absenceListWithoutLocations.filter(f => f.plannerListId !== null).every(i => i.selected);
    handleMyListsChange(areAllListsSelected, checkedLists);

    const selectedDivisionsIds = divisionsFlattenTree.filter(i => i.selected).map(d => d.plannerListId);
    handleDivisionsChange(selectedDivisionsIds);

    setSmartPreferencesChecked(absenceSettings.showLastTenEmployees);
    setDisplayNewListsChecked(absenceSettings.displayAllNewListsFromMyListCategory);

  }, [absenceSettings]);

  const isThereAnyChange = (arr1: Key[], arr2: Key[]) => arr1.sort().toString() !== arr2.sort().toString();

  const areMyListsModified = () => {
    const initialCheckedListsIds = absenceListWithoutLocations.filter(f => f.selected).map(i => i.plannerListId);
    return isThereAnyChange(initialCheckedListsIds, myLists.checkedKeys);
  };

  const areDivisionsModified = () => {
    const initialDivisionsIds = divisionsFlattenTree.filter(i => i.selected).map(d => d.plannerListId);
    return isThereAnyChange(initialDivisionsIds, divisions.checkedKeys);
  };

  const areLocationsModified = () => {
    const initialLocationsIds = absenceListWithOnlyLocations.filter(f => f.selected).map(d => d.plannerListId);
    return isThereAnyChange(initialLocationsIds, locations.checkedKeys);
  };

  const handleMyListsOpen = (open: boolean) => {
    setMyListsOpen(open);
    if (open) {
      setLocationsOpen(false);
      setDivisionsOpen(false);
    }
  };

  const handleIbaGroupOpen = (open: boolean) => {
    setDivisionsOpen(open);
    if (open) {
      setMyListsOpen(false);
      setLocationsOpen(false);
    }
  };

  const handleLocationsOpen = (open: boolean) => {
    setLocationsOpen(open);
    if (open) {
      setDivisionsOpen(false);
      setMyListsOpen(false);
    }
  };

  const treeLists = useMemo(() => {
    const grouped = groupBy((i: AbsenceListItemI) => i.listType)(absenceListWithoutLocations);
    const toArray: [string, NonEmptyArray<AbsenceListItemI>][] = [];
    if (grouped.CIRCLE) {
      toArray.push(['CIRCLE', grouped.CIRCLE]);
    }
    if (grouped.PROJECT) {
      toArray.push(['PROJECT', grouped.PROJECT]);
    }
    if (grouped.DIVISION) {
      toArray.push(['DIVISION', grouped.DIVISION]);
    }
    if (grouped.PLANNER) {
      toArray.push(['PLANNER', grouped.PLANNER]);
    }

    return [
      {
        title: 'All My Lists',
        key: ALL,
        className: ROOT_CLASS,
        children: toArray
          .filter(([group, list]) => list.length && group !== 'ALL')
          .map(([group, data]) => {
            const isPlanner = group === 'PLANNER';

            const nodeTitle = isPlanner ?
              <div className='tree-node-with-link'>
                <span className='title-name'>{t(group)}</span>
                <div onClick={(e) => e.stopPropagation()}>
                  <NewListLink
                    to={`${PLANNER_ROUTE}?doShowNewListModal=true`}
                    openInNewTab={true}
                  />
                </div>
              </div>
              :
              <span className='title-name'>{t(group)}</span>
              ;

            return {
              title: nodeTitle,
              key: t(group),
              children: data.map((i) => ({
                title: i.listName,
                key: i.plannerListId !== null ? i.plannerListId : i.listName,
                type: i.listType
              }))
            };
          }
          )
      },
    ];
  },[absenceSettings.filtersList]);

  const treeDivisions = useMemo(() => {
    return [{...absenceSettings.filtersDivisionsTree, className: ROOT_CLASS}]; 
  }, [absenceSettings.filtersDivisionsTree]);

  const treeLocations = useMemo(() => {
    return [
      {
        title: 'Locations',
        key: ALL,
        className: ROOT_CLASS,
        children: absenceListWithOnlyLocations.map(i => ({
          title: i.listName,
          key: i.plannerListId,
          type: i.listType
        }))
      }
    ];
  },[absenceSettings.filtersList]);

  const onCheckMyLists: TreeProps['onCheck'] = (_, info) => {
    const isAllSelected = !!info.checkedNodes.filter(i => (i.key === ALL)).length;
    const checkedLists = info.checkedNodes.filter(i => (!i.children)).map(i => ({
      key: i.key,
      title: i.title as string,
      type: (i as DataNodeExtended).type,
    }));
    handleMyListsChange(isAllSelected, checkedLists);
  };

  const onCheckDivisions: TreeProps['onCheck'] = (checkedKeys) => {
    const keys = (checkedKeys as Key[]);
    handleDivisionsChange(keys);
  };

  const onCheckLocations: TreeProps['onCheck'] = (_, info) => {
    const isAllSelected = !!info.checkedNodes.filter(i => (i.key === ALL)).length;
    const checkedLocations = info.checkedNodes.filter(i => (!i.children)).map(i => ({key: i.key, title: i.title as string}));
    handleLocationsChange(isAllSelected, checkedLocations);
  };

  const getItemsToSend = (list: AbsenceListItemI[], checkedKeys: Key[]): AbsenceListItemI[] => list.map(i => {
    if (!checkedKeys.includes(i.plannerListId)) {
      return {...i, selected: false};
    }
    return {...i, selected: true};
  });

  const getSelectedDivisionsToSend = () => {
    const flattenTree = getFlattenTree(treeDivisions);
    return getItemsToSend(flattenTree, divisions.checkedKeys).filter(i => i.selected);
  };

  const isRestoreBtnVisible = isAbsenceSettingsFetched &&
    (!smartPreferencesChecked || !displayNewListsChecked || !myLists.isAllSelected ||
      locations.checkedKeys.length || divisions.checkedKeys.length);

  const isSaveBtnDisabled = (absenceSettings.showLastTenEmployees === smartPreferencesChecked) &&
    (displayNewListsChecked === absenceSettings.displayAllNewListsFromMyListCategory) &&
    !areMyListsModified() && !areDivisionsModified() && !areLocationsModified();

  return(
    <>
      <Modal visible={true}
        loading={!isAbsenceSettingsFetched}
        className={'absenseSettingsModal'}
        footer={[
          <button className={`absenseSettingsModal__restoreBtn ${isRestoreBtnVisible ? '' : 'absenseSettingsModal__restoreBtn_invisible'}`}
            onClick={() => {
              setDefaultSettingsVisible(true);
            }}>
            {t('absenceSettingsModal.restoreBtn')}
          </button>,
          <Button theme={{ variant: 'neutral', color: 'grey' }}
            onClick={() => {
              onClose?.();
            }}>
            {t('cancel')}
          </Button>,
          <Button theme={{ variant: 'primary', color: 'green' }}
            disabled={isSaveBtnDisabled}
            onClick={() => {
              updateAbsenceSettings(
                getItemsToSend(absenceListWithoutLocations, myLists.checkedKeys),
                getItemsToSend(absenceListWithOnlyLocations, locations.checkedKeys),
                getSelectedDivisionsToSend(),
                smartPreferencesChecked,
                displayNewListsChecked,
                allCirclesListsChosen,
                allProjectListsChosen,
                allDivisionListsChosen,
                allPlannerListsChosen
              );
              onClose?.();
              onSave();
            }}>
            {t('save')}
          </Button>
        ]}
        titleText={t('absenceSettingsModal.title')}
        onClose={() => onClose?.()}>
        <div className={'absenseSettingsModal__body'}>
          {isAbsenceSettingsFetched && <>
            <div className={'absenseSettingsModal__preferences'}>
              <div className={'absenseSettingsModal__preferences__top'}>
                <SmartPreferencesSvg />
                <span>{t('absenceSettingsModal.smartPreferencesTitle')}</span>
              </div>
              <Checkbox
                className='absenseSettingsModal__preferences__checkbox'
                setChecked={(v) => setSmartPreferencesChecked(v)}
                label={t('absenceSettingsModal.smartPreferencesLabel')}
                checked={smartPreferencesChecked}
              />
            </div>
            <Accordion
              className={'accordion_mylists'}
              name={t('absenceSettingsModal.myLists')}
              open={myListsOpen}
              setOpen={handleMyListsOpen}
              info={myLists.tag}
            >
              <Tree
                className={'accordion__tree'}
                checkable
                treeData={treeLists}
                checkedKeys={myLists.checkedKeys}
                onCheck={onCheckMyLists}
                switcherIcon={<DownOutlined />}
                defaultExpandedKeys={[ALL]}
              />
              <Divider className={'accordion_divider'} />
              <Checkbox
                setChecked={(v) => setDisplayNewListsChecked(v)}
                label={t('absenceSettingsModal.displayNewLists')}
                checked={displayNewListsChecked}
              />
            </Accordion>

            <Accordion
              className={'accordion_ibagroup'}
              name={t('absenceSettingsModal.ibaGroup')}
              open={divisionsOpen}
              setOpen={handleIbaGroupOpen}
              info={divisions.tag}
            >
              <Tree
                className={'accordion__tree'}
                checkable
                treeData={treeDivisions as unknown as DataNode[]}
                checkedKeys={divisions.checkedKeys}
                onCheck={onCheckDivisions}
                switcherIcon={<DownOutlined />}
                fieldNames={{ title: 'listName', key: 'plannerListId', children: 'subFilterDtos' }}
                defaultExpandedKeys={[ROOT_DIVISIONS]}
              />
            </Accordion>

            <Accordion
              className={'accordion_locations'}
              name={t('absenceSettingsModal.locations')}
              open={locationsOpen}
              setOpen={handleLocationsOpen}
              info={locations.tag}
            >
              <Tree
                className={'accordion__tree'}
                checkable
                treeData={treeLocations}
                checkedKeys={locations.checkedKeys}
                onCheck={onCheckLocations}
                switcherIcon={<DownOutlined />}
                defaultExpandedKeys={[ALL]}
              />
            </Accordion>
          </>}
        </div>
      </Modal>
      <RestoreDefaultSettingsModal
        isVisible={defaultSettingsVisible} 
        onClose={() => setDefaultSettingsVisible(false) }
        onRestore={() => {
          setSmartPreferencesChecked(true);
          const allMyLists = absenceListWithoutLocations.map(i => ({
            key: i.plannerListId,
            title: i.listName,
            type: i.listType
          }));
          handleMyListsChange(true, allMyLists);
          setDisplayNewListsChecked(true);
          handleDivisionsChange([]);
          handleLocationsChange(false, []);
        }}
      />
    </>
  );
});